/**
 * Converts a nested `pricePeriods` structure into a flat `prices` list.
 *
 * @param {Array} pricePeriods - Example:
 * [
 *   {
 *     mstart: 1, mend: 5, dstart: 1, dend: 31, rateType: 'PROOM',
 *     rates: [
 *       { type: 'DEFAULT', value: '$100.00', cur: 'USD' }
 *     ]
 *   },
 *   ...
 * ]
 *
 * @returns {Array} A flat list of prices. Example:
 * [
 *   {
 *     mstart: 1, mend: 5, dstart: 1, dend: 31,
 *     roomType: 'DEFAULT',
 *     rate: { type: 'PROOM', value: '$100.00', cur: 'USD' }
 *   },
 *   ...
 * ]
 */
function convertToPrices(pricePeriods) {
  const prices = [];
  pricePeriods.forEach((item) => {
    const { mstart, mend, dstart, dend, rateType, rates } = item;
    rates.forEach((rate) => {
      prices.push({
        mstart,
        mend,
        dstart,
        dend,
        roomType: rate.type,
        rate: {
          type: rateType,
          value: rate.value,
          cur: rate.cur,
        },
      });
    });
  });
  return prices;
}

/**
 * Converts a flat `prices` list back into the grouped `pricePeriods` structure.
 * Groups items by the same date range and rate type.
 *
 * @param {Array} prices - Example:
 * [
 *   {
 *     mstart: 1, mend: 5, dstart: 1, dend: 31,
 *     roomType: 'DEFAULT',
 *     rate: { type: 'PROOM', value: '$100.00', cur: 'USD' }
 *   },
 *   ...
 * ]
 *
 * @returns {Array} Grouped pricePeriods. Example:
 * [
 *   {
 *     mstart: 1, mend: 5, dstart: 1, dend: 31, rateType: 'PROOM',
 *     rates: [
 *       { type: 'DEFAULT', value: '$100.00', cur: 'USD' }
 *     ]
 *   },
 *   ...
 * ]
 */
function convertToPricePeriods(prices) {
  const pricePeriods = [];
  prices.forEach((item) => {
    const key = `${item.mstart}-${item.mend}-${item.dstart}-${item.dend}-${item.rate.type}`;
    const existingPeriod = pricePeriods.find(
      (g) => `${g.mstart}-${g.mend}-${g.dstart}-${g.dend}-${g.rateType}` === key
    );
    const rateEntry = { type: item.roomType, value: item.rate.value, cur: item.rate.cur };

    if (existingPeriod) {
      existingPeriod.rates.push(rateEntry);
    } else {
      pricePeriods.push({
        mstart: item.mstart,
        mend: item.mend,
        dstart: item.dstart,
        dend: item.dend,
        rateType: item.rate.type,
        rates: [rateEntry],
      });
    }
  });
  return pricePeriods;
}

/**
 * Filters the prices list, keeping only the prices with 'DEFAULT' or empty rate types.
 *
 * - If a price period contains a rate with 'DEFAULT' type, only that rate is kept.
 * - If no 'DEFAULT' rate is found, but there is a rate with an empty or null type, that rate is kept.
 * - Periods with no valid rates are filtered out.
 *
 * @param {Array} prices - A flat list of prices, each containing rate details.
 *
 * @returns {Array} A filtered flat list of prices containing only the rates with 'DEFAULT' or empty types.
 */
function filterPricesWithDefaultOrEmptyType(prices) {
  let pricePeriods = convertToPricePeriods(prices);

  for (let i = 0; i < pricePeriods.length; i++) {
    const rates = pricePeriods[i].rates;
    const defaultRate = rates.find((rate) => rate.type === "DEFAULT");
    const doubleRate = rates.find((rate) => rate.type === "DOUBLE_QK");
    const emptyRate = rates.find((rate) => rate.type === null || rate.type === "" || rate.type === undefined);

    // Set the rates to 'DEFAULT' rate if found, else use the empty rate if found, otherwise an empty array
    pricePeriods[i].rates = defaultRate ? [defaultRate] : doubleRate ? [doubleRate] : emptyRate ? [emptyRate] : [];
  }

  // Remove any price period with no valid rates
  pricePeriods = pricePeriods.filter((period) => period.rates.length !== 0);

  return convertToPrices(pricePeriods);
}

/** Preferred order when adding a new PROOM room-type row. */
const PROOM_ROOM_TYPES = ["DOUBLE_QK", "SINGLE_DK", "DOUBLE_DOUBLE", "TRIPLE", "QUAD"];

/**
 * Returns room types that appear more than once (ignores empty/null).
 * @param {string[]} roomTypes
 * @returns {string[]}
 */
function findDuplicateRoomTypes(roomTypes) {
  const seen = new Set();
  const dupes = new Set();
  for (const type of roomTypes || []) {
    if (type == null || type === "") continue;
    if (seen.has(type)) dupes.add(type);
    else seen.add(type);
  }
  return [...dupes];
}

/**
 * First PROOM room type not already used, or null if all are taken.
 * @param {string[]} usedTypes
 * @returns {string|null}
 */
function getNextUnusedRoomType(usedTypes) {
  const used = new Set((usedTypes || []).filter((t) => t != null && t !== ""));
  return PROOM_ROOM_TYPES.find((type) => !used.has(type)) || null;
}

/**
 * Filter room-type dropdown options: keep the current row's value, hide types used on other rows.
 * @param {Array<{label: string, value: string}>} options
 * @param {string[]} usedByOthers
 * @param {string} [currentValue]
 */
function filterAvailableRoomTypeOptions(options, usedByOthers, currentValue) {
  const used = new Set((usedByOthers || []).filter((t) => t != null && t !== ""));
  return (options || []).filter((opt) => opt.value === currentValue || !used.has(opt.value));
}

export default {
  convertToPrices,
  convertToPricePeriods,
  filterPricesWithDefaultOrEmptyType,
  PROOM_ROOM_TYPES,
  findDuplicateRoomTypes,
  getNextUnusedRoomType,
  filterAvailableRoomTypeOptions,
};
